import type { SubscribePayload } from 'graphql-ws';
import {
	type CacheConfig,
	Observable,
	type RequestParameters,
	type SubscribeFunction,
	type Variables,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { getClient, getSinkError, type SinkError } from '@atlassian/graphql-subscription';

export type SubscribeOptions = {
	subscribeExtensions?: Record<string, string>;
	onSubscribeTrigger?: (payload: SubscribePayload) => void;
	onSubscribeError?: (payload: SubscribePayload & { requestId: string | null }) => void;
};

// AGG de-duplicates subscription delivery by channel — the wire shape
// (pq, operationName, variables, extensions) — per connection, not per Relay
// operation. Two observers of the same channel therefore starve each other
// (AFB-2118). To work around it we keep one upstream subscription per channel
// and fan its events out to every local observer, ref-counted so the upstream is
// torn down only when the last observer leaves. Gated on
// `jira_relay_subscription_dedupe_fanout`; when off, each observer gets its own
// upstream (unchanged behaviour).

type LocalSink = {
	next: (value: unknown) => void;
	// requestId is passed in by the upstream error handler (captured before the
	// channel is dropped) so it can't be looked up from an already-deleted channel.
	error: (err: unknown, requestId: string | null) => void;
	complete?: () => void;
};

type Channel = {
	sinks: Set<LocalSink>;
	dispose: () => void;
	lastRequestId: string | null;
};

const channels = new Map<string, Channel>();

// Order-independent so two callers with the same channel produce the same key
// regardless of property insertion order.
function stableStringify(value: unknown): string {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value) ?? 'null';
	}
	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}
	const obj = value as Record<string, unknown>;
	const keys = Object.keys(obj).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

function channelKey(payload: SubscribePayload): string {
	return stableStringify({
		pq: (payload.extensions as Record<string, unknown> | undefined)?.pq ?? null,
		operationName: payload.operationName ?? null,
		variables: payload.variables ?? null,
		extensions: payload.extensions ?? null,
	});
}

let cachedDefaultSubscribe: SubscribeFunction | undefined;

export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction;
export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction {
	if (typeof subscribeOptions === 'undefined' && typeof cachedDefaultSubscribe !== 'undefined') {
		return cachedDefaultSubscribe;
	}

	const fn: SubscribeFunction = (
		{ id, name: operationName }: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	) =>
		Observable.create((sink) => {
			const client = getClient();
			if (!client) {
				return sink.error(new Error('Unable to create subscription client'));
			}
			const replayConfig = cacheConfig?.metadata?.replay || undefined;

			const replayCommand =
				replayConfig !== null && typeof replayConfig === 'object' && 'command' in replayConfig
					? (replayConfig as Record<string, unknown>).command
					: undefined;
			const replayExtensions = replayCommand
				? { command: replayCommand }
				: replayConfig
					? { replay: replayConfig }
					: {};

			const payload: SubscribePayload = {
				query: '',
				operationName,
				variables,
				extensions: {
					pq: id,
					...(subscribeOptions?.subscribeExtensions || {}),
					...replayExtensions,
				},
			};

			subscribeOptions?.onSubscribeTrigger?.(payload);

			// Gate off (default): one upstream per observer (original behaviour).
			if (!fg('jira_relay_subscription_dedupe_fanout')) {
				let requestId: string | null = null;
				return client.subscribe(payload, {
					...sink,
					next: (value) => {
						// @ts-expect-error agg prop is missing on generic "extensions" type
						const aggRequestId = value?.extensions?.gateway?.request_id;
						requestId = aggRequestId ?? requestId;
						// @ts-expect-error incompatible types between two libraries
						sink.next(value);
					},
					error: (sinkError: SinkError) => {
						const errorInfo = getSinkError(sinkError);
						sink.error(errorInfo);
						subscribeOptions?.onSubscribeError?.({ ...payload, requestId });
					},
				});
			}

			// Fan-out on: share one upstream per channel across all observers.
			const key = channelKey(payload);

			const localSink: LocalSink = {
				next: (value) => {
					// @ts-expect-error incompatible types between two libraries
					sink.next(value);
				},
				error: (err, requestId) => {
					sink.error(err as Error);
					subscribeOptions?.onSubscribeError?.({ ...payload, requestId });
				},
				complete: () => sink.complete?.(),
			};

			const existingChannel = channels.get(key);

			if (!existingChannel) {
				// First observer for this channel: open the single upstream sub.
				// The handlers below close over `newChannel`, so it must exist before
				// client.subscribe runs; but we only register it in the map after its
				// real `dispose` is assigned, so a dispose can never hit a no-op
				// placeholder (and never leak the upstream subscription).
				const newChannel: Channel = {
					sinks: new Set([localSink]),
					dispose: () => {},
					lastRequestId: null,
				};

				newChannel.dispose = client.subscribe(payload, {
					next: (value) => {
						// @ts-expect-error agg prop is missing on generic "extensions" type
						const aggRequestId = value?.extensions?.gateway?.request_id;
						if (aggRequestId) {
							newChannel.lastRequestId = aggRequestId;
						}
						// Snapshot so a sink unsubscribing during dispatch doesn't
						// mutate the set we're iterating.
						for (const s of Array.from(newChannel.sinks)) {
							try {
								s.next(value);
							} catch {
								// Isolate sinks: a buggy consumer must not starve siblings.
							}
						}
					},
					error: (sinkError: SinkError) => {
						const errorInfo = getSinkError(sinkError);
						// Snapshot the sinks and the last gateway request id before
						// dropping the channel, so a sink that resubscribes inside its
						// error handler opens a fresh upstream while still receiving the
						// request id from the failed subscription.
						const sinks = Array.from(newChannel.sinks);
						const { lastRequestId } = newChannel;
						channels.delete(key);
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.error(errorInfo, lastRequestId);
							} catch {
								/* isolate sinks */
							}
						}
					},
					complete: () => {
						const sinks = Array.from(newChannel.sinks);
						channels.delete(key);
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.complete?.();
							} catch {
								/* isolate sinks */
							}
						}
					},
				});

				// Register only now that dispose is set (see note above).
				channels.set(key, newChannel);
			} else {
				// Subsequent observers for the same channel just attach their sink.
				existingChannel.sinks.add(localSink);
			}

			// Relay dispose: drop this observer; only tear down the upstream when
			// the last observer for the channel goes away.
			return () => {
				const c = channels.get(key);
				if (!c) {
					return;
				}
				c.sinks.delete(localSink);
				if (c.sinks.size === 0) {
					channels.delete(key);
					try {
						c.dispose();
					} catch {
						/* upstream dispose errors must not break Relay teardown */
					}
				}
			};
		});

	if (typeof subscribeOptions === 'undefined') {
		cachedDefaultSubscribe = fn;
	}

	return fn;
}

export const subscribe: SubscribeFunction = createSubscribeFn();
